import React from 'react';
import {
  ArrowUpRight,
  Instagram,
  Play,
  Clapperboard,
  Users,
  Film,
} from 'lucide-react';
import { NANCREATED } from '../mock';

/**
 * NancreatedBento — a full-width editorial bento for Nancreated.
 * Mirrors the BKKButter bento's 12-col × 3-row structure for rhythm.
 * 1 featured 9:16 reel player + 3 mini 9:16 reel slots = 4 video placeholders.
 */
export default function NancreatedBento() {
  const n = NANCREATED;
  const [s1, s2] = n.stats;

  return (
    <section
      id="nancreated"
      className="py-24 md:py-32"
      style={{ background: 'var(--bone-soft)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
          <div className="md:col-span-6">
            <div
              className="mono text-[11px] uppercase tracking-[0.2em] mb-4"
              style={{ color: 'var(--terracotta)' }}
            >
              § 03.6 · Another closer look
            </div>
            <h2
              className="display"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.6rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: 'var(--ink)',
              }}
            >
              Nancreated,{' '}
              <em
                className="serif"
                style={{ fontStyle: 'italic', color: 'var(--forest)', fontWeight: 400 }}
              >
                filming, then sharing.
              </em>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p
              className="text-[15px] md:text-[17px] leading-relaxed"
              style={{ color: 'var(--forest-soft)' }}
            >
              I started filming because I could not explain things in words. Now I do both. A camera is how I notice. Screenings are how I share. The hardest part is knowing when something is worth recording.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div
          className="grid grid-cols-12 gap-3 md:gap-4"
          style={{ gridAutoRows: 'minmax(150px, auto)' }}
        >
          {/* T1 — Hero film still (tall) */}
          <Tile
            className="col-span-12 md:col-span-5 md:row-span-2"
            padded={false}
            aspect="4/5"
          >
            <img
              src={n.heroImage}
              alt={n.heroAlt}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15,27,20,0.1) 0%, rgba(15,27,20,0) 30%, rgba(15,27,20,0.65) 100%)',
              }}
            />
            {/* Film sprocket strip decoration */}
            <FilmSprocketStrip />
            <div
              className="absolute top-4 left-4 inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full"
              style={{ background: 'var(--cream)', color: 'var(--forest)' }}
            >
              <Clapperboard size={11} strokeWidth={1.75} style={{ color: 'var(--terracotta)' }} />
              {n.kicker}
            </div>
            <div
              className="absolute bottom-4 left-4 mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--cream)' }}
            >
              Still · behind the camera · ’24
            </div>
            <a
              href={n.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open @nancreated on Instagram"
              className="absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: 'var(--cream)',
                boxShadow: '0 10px 30px -10px rgba(15,27,20,0.35)',
              }}
            >
              <ArrowUpRight size={16} style={{ color: 'var(--forest)' }} />
            </a>
          </Tile>

          {/* T2 — Headline */}
          <Tile
            className="col-span-12 md:col-span-4 md:row-span-1 flex flex-col justify-between gap-5"
            bg="cream"
          >
            <div
              className="mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--terracotta)' }}
            >
              the practice
            </div>
            <h3
              className="display"
              style={{
                fontSize: 'clamp(1.3rem, 2.2vw, 1.85rem)',
                lineHeight: 1.12,
                fontWeight: 500,
                color: 'var(--ink)',
                letterSpacing: '-0.015em',
              }}
            >
              A camera is how I{' '}
              <em
                className="serif"
                style={{ fontStyle: 'italic', color: 'var(--forest)', fontWeight: 500 }}
              >
                notice
              </em>
              . A room is how I{' '}
              <em
                className="serif"
                style={{ fontStyle: 'italic', color: 'var(--terracotta)', fontWeight: 500 }}
              >
                share
              </em>
              .
            </h3>
            <p
              className="mono text-[11px] uppercase tracking-[0.14em]"
              style={{ color: 'var(--moss)' }}
            >
              {n.subtitle}
            </p>
          </Tile>

          {/* T3 — Featured 9:16 reel player (tall) */}
          <Tile
            className="col-span-12 md:col-span-3 md:row-span-3"
            bg="ink"
            padded={false}
          >
            <div className="absolute inset-0 p-4 flex items-center justify-center">
              <ReelPlayer reel={n.featuredReel} featured />
            </div>
          </Tile>

          {/* T4 — Stat (forest) */}
          <StatTile
            label={s1.label}
            value={s1.value}
            unit={s1.unit}
            bg="var(--forest)"
            valueColor="var(--cream)"
            labelColor="#d4b896"
            unitColor="#d4cbbc"
            icon={<Film size={13} strokeWidth={1.75} />}
          />

          {/* T5 — Stat (terracotta) */}
          <StatTile
            label={s2.label}
            value={s2.value}
            unit={s2.unit}
            bg="var(--terracotta)"
            valueColor="var(--cream)"
            labelColor="rgba(250,246,238,0.7)"
            unitColor="rgba(250,246,238,0.85)"
            icon={<Users size={13} strokeWidth={1.75} />}
          />

          {/* T6 — Quote */}
          <Tile
            className="col-span-12 md:col-span-5 flex flex-col justify-between gap-6"
            bg="bone"
          >
            <div
              className="mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--moss)' }}
            >
              a note from behind the viewfinder
            </div>
            <blockquote
              className="serif"
              style={{
                fontSize: 'clamp(1.05rem, 1.5vw, 1.3rem)',
                lineHeight: 1.45,
                fontStyle: 'italic',
                color: 'var(--ink)',
                fontWeight: 400,
                letterSpacing: '-0.005em',
              }}
            >
              “Every shot is a small bet on what someone will feel three
              seconds in.{' '}
              <span style={{ color: 'var(--forest)' }}>
                Most of the job is not pressing record
              </span>{' '}
              until you know the bet.”
            </blockquote>
            <div
              className="mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--stone)' }}
            >
              {n.quote.attribution}
            </div>
          </Tile>

          {/* T7 — Recent reels (3 × 9:16 mini players) */}
          <Tile
            className="col-span-12 md:col-span-4 flex flex-col gap-4"
            bg="cream"
          >
            <div className="flex items-center justify-between">
              <div
                className="mono text-[10px] uppercase tracking-[0.22em]"
                style={{ color: 'var(--terracotta)' }}
              >
                recent reels
              </div>
              <div
                className="mono text-[10px] tracking-wide"
                style={{ color: 'var(--stone)' }}
              >
                9:16 · tap to open
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2.5 flex-1">
              {n.recentReels.map((r, i) => (
                <ReelThumb key={i} reel={r} index={i + 2} />
              ))}
            </div>
          </Tile>
        </div>

        {/* Footnote strip */}
        <div
          className="mt-8 md:mt-10 flex flex-wrap items-center justify-between gap-4 pt-6 border-t"
          style={{ borderColor: 'rgba(15,27,20,0.08)' }}
        >
          <div
            className="mono text-[11px] uppercase tracking-[0.2em] flex items-center gap-3 flex-wrap"
            style={{ color: 'var(--moss)' }}
          >
            <span>Next screening: TBD · Bangkok</span>
            <span style={{ color: 'var(--stone)' }}>·</span>
            <span style={{ color: 'var(--stone)' }}>{n.press}</span>
          </div>
          <a
            href={n.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.18em] link-line"
            style={{ color: 'var(--forest)' }}
          >
            <Instagram size={13} strokeWidth={1.75} /> @nancreated
            <ArrowUpRight size={13} strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============== helpers ============== */

function Tile({ children, className = '', bg = 'bone', padded = true, aspect }) {
  const bgMap = {
    bone: 'var(--bone)',
    boneSoft: 'var(--bone-soft)',
    boneDeep: 'var(--bone-deep)',
    cream: 'var(--cream)',
    ink: 'var(--ink)',
  };
  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${
        padded ? 'p-5 md:p-6' : ''
      } ${className}`}
      style={{
        background: bgMap[bg] || bg,
        border: '1px solid rgba(15,27,20,0.06)',
        boxShadow: '0 1px 0 rgba(15,27,20,0.02)',
        aspectRatio: aspect,
      }}
    >
      {children}
    </div>
  );
}

function StatTile({ label, value, unit, bg, valueColor, labelColor, unitColor, icon }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 md:p-6 col-span-6 md:col-span-2 flex flex-col justify-between gap-4"
      style={{
        background: bg,
        border: '1px solid rgba(15,27,20,0.06)',
        boxShadow: '0 1px 0 rgba(15,27,20,0.02)',
      }}
    >
      <div
        className="mono text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5"
        style={{ color: labelColor }}
      >
        {icon}
        {label}
      </div>
      <div
        className="display"
        style={{
          fontSize: 'clamp(2.2rem, 3.2vw, 3rem)',
          lineHeight: 0.95,
          fontWeight: 400,
          color: valueColor,
          letterSpacing: '-0.025em',
        }}
      >
        {value}
      </div>
      <div className="text-[12px] leading-snug" style={{ color: unitColor }}>
        {unit}
      </div>
    </div>
  );
}

/**
 * Big featured 9:16 reel player.
 * Swap in a real video by passing reel.src (and reel.poster for a still frame).
 */
function ReelPlayer({ reel, featured = false }) {
  const [failed, setFailed] = React.useState(false);
  const showVideo = reel.src && !failed;
  return (
    <div
      className="relative w-full rounded-[18px] overflow-hidden"
      style={{
        aspectRatio: '9 / 16',
        maxWidth: 260,
        background:
          'radial-gradient(140% 100% at 50% 0%, rgba(168,89,58,0.18) 0%, rgba(168,89,58,0) 45%), linear-gradient(180deg, #1a241d 0%, #0f1512 100%)',
        border: '1px solid rgba(244,239,230,0.08)',
        boxShadow:
          '0 30px 60px -30px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.04) inset',
      }}
    >
      {showVideo ? (
        <video
          src={reel.src}
          poster={reel.poster || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : reel.poster ? (
        <img
          src={reel.poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
      ) : (
        <FilmGrain />
      )}

      {/* Gradient overlay for chrome */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(15,21,18,0.4) 0%, rgba(15,21,18,0) 30%, rgba(15,21,18,0) 70%, rgba(15,21,18,0.85) 100%)',
        }}
      />

      {/* Top label */}
      <div
        className="absolute top-3 left-3 mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-full inline-flex items-center gap-1.5"
        style={{
          background: 'rgba(15,21,18,0.55)',
          backdropFilter: 'blur(8px)',
          color: 'var(--cream)',
          border: '1px solid rgba(244,239,230,0.08)',
        }}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{
            background: '#e11d48',
            boxShadow: '0 0 0 3px rgba(225,29,72,0.25)',
          }}
        />
        {reel.label || 'Reel'}
      </div>

      {/* Center play button — hidden when video is autoplaying */}
      {!showVideo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="rounded-full flex items-center justify-center"
            style={{
              width: featured ? 56 : 44,
              height: featured ? 56 : 44,
              background: 'rgba(244,239,230,0.92)',
              boxShadow: '0 6px 20px -6px rgba(0,0,0,0.45)',
              transition: 'transform 0.3s ease',
            }}
          >
            <Play
              size={featured ? 20 : 16}
              strokeWidth={2}
              style={{
                color: 'var(--ink)',
                marginLeft: 2,
                fill: 'var(--ink)',
              }}
            />
          </div>
        </div>
      )}

      {/* Bottom title + scrubber */}
      <div className="absolute bottom-0 inset-x-0 p-3">
        {reel.title && (
          <div
            className="serif mb-1.5"
            style={{
              color: 'var(--cream)',
              fontSize: 13,
              lineHeight: 1.2,
              fontStyle: 'italic',
              fontWeight: 500,
            }}
          >
            {reel.title}
          </div>
        )}
        <div className="flex items-center gap-2 mb-1.5">
          <div
            className="h-[2px] flex-1 rounded-full overflow-hidden"
            style={{ background: 'rgba(244,239,230,0.2)' }}
          >
            <div
              className="h-full rounded-full"
              style={{ width: '32%', background: 'var(--cream)' }}
            />
          </div>
          <span
            className="mono"
            style={{ fontSize: 9, color: 'var(--cream)', opacity: 0.75 }}
          >
            {reel.duration || '··:··'}
          </span>
        </div>
      </div>
    </div>
  );
}

function ReelThumb({ reel, index }) {
  const [failed, setFailed] = React.useState(false);
  const showVideo = reel.src && !failed;
  return (
    <div
      className="relative overflow-hidden rounded-lg group cursor-pointer"
      style={{
        aspectRatio: '9 / 16',
        background:
          'radial-gradient(120% 80% at 50% 0%, rgba(31,59,45,0.25) 0%, rgba(31,59,45,0) 50%), linear-gradient(180deg, #1a241d 0%, #0f1512 100%)',
        border: '1px solid rgba(15,27,20,0.08)',
      }}
    >
      {showVideo ? (
        <video
          src={reel.src}
          poster={reel.poster || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : reel.poster ? (
        <img
          src={reel.poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-75"
        />
      ) : (
        <FilmGrain />
      )}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(15,21,18,0.1) 0%, rgba(15,21,18,0) 40%, rgba(15,21,18,0.85) 100%)',
        }}
      />

      {/* Play dot — only when no video */}
      {!showVideo && (
        <div className="absolute top-2 left-2">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(244,239,230,0.9)' }}
          >
            <Play
              size={10}
              strokeWidth={2}
              style={{ color: 'var(--ink)', marginLeft: 1, fill: 'var(--ink)' }}
            />
          </div>
        </div>
      )}

      {/* Index badge */}
      <div
        className="absolute top-2 right-2 mono"
        style={{
          fontSize: 9,
          letterSpacing: '0.18em',
          color: 'var(--cream)',
          opacity: 0.7,
        }}
      >
        0{index}
      </div>

      {/* Caption */}
      <div
        className="absolute bottom-2 inset-x-2"
        style={{
          fontSize: 10,
          lineHeight: 1.2,
          color: 'var(--cream)',
          letterSpacing: '-0.005em',
        }}
      >
        {reel.caption}
      </div>
    </div>
  );
}

/**
 * Decorative film sprocket strip along the bottom of the hero tile.
 */
function FilmSprocketStrip() {
  const holes = Array.from({ length: 16 });
  return (
    <div
      className="absolute left-0 right-0 bottom-14 flex items-center gap-1 px-4"
      style={{ pointerEvents: 'none', opacity: 0.4 }}
      aria-hidden
    >
      {holes.map((_, i) => (
        <span
          key={i}
          className="rounded-sm shrink-0"
          style={{
            width: 12,
            height: 8,
            background: 'rgba(250,246,238,0.9)',
            mixBlendMode: 'overlay',
          }}
        />
      ))}
    </div>
  );
}

/**
 * Subtle animated film-grain placeholder for empty video tiles.
 */
function FilmGrain() {
  return (
    <div
      className="absolute inset-0"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        opacity: 0.12,
        mixBlendMode: 'screen',
      }}
      aria-hidden
    />
  );
}
